import {FC, PropsWithChildren, useRef, useState} from "react";
import {placement} from "@material-tailwind/react/types/components/menu";

interface Props {
  text?: string;
  title?: string;
  subtitle?: string;
  position: placement;
  className?: string;
  wrapperClassName?: string;
  withoutPointer?: boolean;
}

const BG = "#282D4D";
const GAP = 8; // px between anchor and tooltip (including arrow)

type Coords = {top?: number; bottom?: number; left?: number; right?: number};

function getCoords(rect: DOMRect, base: string): Coords {
  switch (base) {
    case "bottom":
      return {top: rect.bottom + GAP, left: rect.left + rect.width / 2};
    case "left":
      return {top: rect.top + rect.height / 2, right: window.innerWidth - rect.left + GAP};
    case "right":
      return {top: rect.top + rect.height / 2, left: rect.right + GAP};
    case "top":
    default:
      return {bottom: window.innerHeight - rect.top + GAP, left: rect.left + rect.width / 2};
  }
}

const Tooltip: FC<PropsWithChildren<Props>> = ({
  children,
  text,
  title,
  subtitle,
  position,
  className,
  wrapperClassName,
  withoutPointer = false,
}) => {
  const hasContent = !!(text || title);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<Coords | null>(null);

  const base = (position?.toString() ?? "top").split("-")[0];
  const isVertical = base === "top" || base === "bottom";

  const show = () => {
    if (!wrapperRef.current || !hasContent) return;
    setCoords(getCoords(wrapperRef.current.getBoundingClientRect(), base));
  };
  const hide = () => setCoords(null);

  const content = title ? (
    <>
      <p className="font-semibold text-white text-xs leading-snug">{title}</p>
      {subtitle && <p className="text-white/70 text-xs mt-0.5 leading-snug">{subtitle}</p>}
    </>
  ) : (
    <p className="text-white text-xs">{text}</p>
  );

  /* ── centering transform + flex direction ──────────────────────────────── */
  const translateClass = isVertical ? "-translate-x-1/2" : "-translate-y-1/2";
  const flexDir = isVertical ? "flex-col items-center" : "flex-row items-center";

  const arrow = (
    <span
      className={["w-2 h-2 rotate-45 flex-shrink-0", isVertical ? "-my-1" : "-mx-1"].asClass}
      style={{backgroundColor: BG}}
    />
  );
  const box = (
    <div className={["px-3 py-2 rounded-lg text-xs w-max max-w-xs", className].asClass} style={{backgroundColor: BG}}>
      {content}
    </div>
  );

  const inner =
    base === "top" ? (
      <>
        {box}
        {arrow}
      </>
    ) : base === "right" ? (
      <>
        {arrow}
        {box}
      </>
    ) : base === "left" ? (
      <>
        {box}
        {arrow}
      </>
    ) : (
      <>
        {arrow}
        {box}
      </>
    );

  return (
    <div
      ref={wrapperRef}
      className={["relative inline-block", !withoutPointer && "cursor-pointer", wrapperClassName].asClass}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      {children}

      {coords && (
        <div className={["fixed z-[9999] pointer-events-none flex", flexDir, translateClass].asClass} style={coords}>
          {inner}
        </div>
      )}
    </div>
  );
};

export default Tooltip;
